/**
 * DetalheDaExecucao — cascata de uma execução: etapa → rodada (principal, reforço,
 * reabertura…) → invocações, com agente, custo, tokens (entrada, saída, cache), duração,
 * status e erro; subtotal por etapa e total da execução. Chega-se aqui pelo link de uma
 * invocação ou pela busca do ID.
 */
import { useEffect, useMemo, useState } from 'react';
import { C } from '@/theme/palette';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { dataHora } from '@/lib/format';
import { ehUuid, useObsExecucao } from '@/api/observability';
import { cascataDaExecucao, duracao, inteiro, rotulo } from './formato';
import {
  AvisoSemCusto, Custo, Kpi, Selo, Titulo, Tokens, Vazio, corDoStatus, estilos,
} from './partes';

function ResultadoDaRodada({ r, visiveis }) {
  return (
    <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <Selo cor={r.portao_fechou ? C.ok : C.warn}>
        {r.portao_fechou ? 'portão fechou' : 'portão não fechou'}
      </Selo>
      <span style={{ color: C.neutralDim, fontSize: 12.5 }}>
        {inteiro(r.gaps_abertos)} gap(s) aberto(s) · {inteiro(r.arquivos_novos)} arquivo(s) novo(s)
        {r.prova ? ` · prova: ${r.prova}` : ''}
        {r.verificado === true ? ' · verificado' : r.verificado === false ? ' · não verificado' : ''}
        {r.passo_que_falhou ? ` · falhou em: ${r.passo_que_falhou}` : ''}
        {' · '}custo da etapa nesta rodada: <Custo valor={r.custo_usd} visivel={visiveis} />
        {r.duracao_ms != null ? ` · ${duracao(r.duracao_ms)}` : ''}
      </span>
      {Object.keys(r.gaps_por_regra || {}).length > 0 ? (
        <span style={{ color: C.neutralMuted, fontSize: 12 }}>
          gaps por regra: {Object.entries(r.gaps_por_regra).map(([k, n]) => `${k} (${inteiro(n)})`).join(', ')}
        </span>
      ) : null}
    </span>
  );
}

function Etapa({ e, visiveis, aoAbrirAgente }) {
  const s = estilos();
  return (
    <section data-testid="etapa-da-execucao" style={{
      border: `1px solid ${C.blackBorder}`, borderRadius: 12, marginBottom: 14, background: C.blackElev, overflow: 'hidden',
    }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 14px', borderBottom: `1px solid ${C.blackBorder}` }}>
        <div>
          <strong style={{ color: C.text, fontSize: 15 }}>{e.etapa}</strong>
          <span style={{ color: C.neutralMuted, fontSize: 12.5, marginLeft: 10 }}>
            {inteiro(e.rodadas.length)} rodada(s) · {inteiro(e.loops)} loop(s) · {inteiro(e.total.invocacoes)} invocação(ões)
            {e.total.erros > 0 ? <span style={{ color: C.bad }}> · {inteiro(e.total.erros)} erro(s)</span> : null}
          </span>
        </div>
        <div style={{ color: C.neutralDim, fontSize: 13, fontVariantNumeric: 'tabular-nums' }} data-testid="subtotal-da-etapa">
          <Custo valor={e.total.custoUsd} visivel={visiveis} /> · {duracao(e.total.duracaoMs)} ·{' '}
          {inteiro(e.total.inputTokens)} ent / {inteiro(e.total.outputTokens)} saí
        </div>
      </header>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...s.tabela, borderRadius: 0 }}>
          <thead>
            <tr>
              <th style={s.th}>Quando</th>
              <th style={s.th}>Agente</th>
              <th style={s.th}>Passo</th>
              <th style={s.thNum}>Custo</th>
              <th style={s.thNum}>Tokens</th>
              <th style={s.thNum}>Duração</th>
              <th style={s.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {e.rodadas.map((r) => (
              <RodadaLinhas key={r.chave} r={r} visiveis={visiveis} aoAbrirAgente={aoAbrirAgente} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RodadaLinhas({ r, visiveis, aoAbrirAgente }) {
  const s = estilos();
  return (
    <>
      <tr data-testid="rodada-da-etapa">
        <td colSpan={7} style={{ ...s.td, background: C.blackElev2 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <strong style={{ color: r.tipoDeRodada === 'principal' ? C.text : C.warn }}>
              {rotulo(r.tipoDeRodada)} · rodada {inteiro(r.rodada)}
            </strong>
            <span style={{ color: C.neutralMuted, fontSize: 12.5 }}>
              {inteiro(r.total.invocacoes)} invocação(ões) · <Custo valor={r.total.custoUsd} visivel={visiveis} /> · {duracao(r.total.duracaoMs)}
            </span>
          </div>
          {r.resultados.map((res, i) => (
            <div key={res.id || i} style={{ marginTop: 6 }}><ResultadoDaRodada r={res} visiveis={visiveis} /></div>
          ))}
          {r.resultados.length === 0 && r.tipoDeRodada !== 'delegacao' ? (
            <div style={{ marginTop: 4, color: C.neutralMuted, fontSize: 12 }}>sem resultado de portão registrado para esta rodada</div>
          ) : null}
        </td>
      </tr>
      {r.invocacoes.map((i) => (
        <tr key={i.id || `${i.occurred_at}-${i.passo}`} data-testid="invocacao-da-execucao">
          <td style={{ ...s.td, whiteSpace: 'nowrap', color: C.neutralDim }}>{dataHora(i.occurred_at)}</td>
          <td style={{ ...s.td, wordBreak: 'break-word', paddingLeft: i.subagente_de ? 26 : 10 }}>
            <button type="button" style={s.link} onClick={() => aoAbrirAgente(i.agent_name)}>{i.agent_name}</button>
            {i.subagente_de ? <div style={{ color: C.neutralMuted, fontSize: 12 }}>subagente de {i.subagente_de}</div> : null}
            <div style={{ color: C.neutralMuted, fontSize: 12 }}>
              {rotulo(i.canal)}{i.tier ? ` · ${i.tier}` : ''}{i.resolved_model ? ` · ${i.resolved_model}` : ''}
            </div>
          </td>
          <td style={{ ...s.td, wordBreak: 'break-word' }}>
            {i.passo}
            {i.camada ? <div style={{ color: C.neutralMuted, fontSize: 12 }}>camada: {i.camada}</div> : null}
            {i.turnos != null ? <div style={{ color: C.neutralMuted, fontSize: 12 }}>{inteiro(i.turnos)} turno(s)</div> : null}
          </td>
          <td style={s.tdNum}><Custo valor={i.cost_usd} visivel={visiveis} /></td>
          <td style={s.tdNum}><Tokens item={i} /></td>
          <td style={s.tdNum}>
            {duracao(i.duracao_ms)}
            {i.duracao_api_ms != null ? <div style={{ color: C.neutralMuted, fontSize: 12 }}>API: {duracao(i.duracao_api_ms)}</div> : null}
          </td>
          <td style={{ ...s.td, maxWidth: 360, overflowWrap: 'anywhere' }}>
            <Selo cor={corDoStatus(i.status)}>{rotulo(i.status)}</Selo>
            {i.erro_tipo || i.erro_resumo ? (
              <div style={{ color: C.bad, fontSize: 12.5, marginTop: 4 }}>
                {i.erro_tipo ? <strong>{i.erro_tipo}</strong> : null}
                {i.erro_tipo && i.erro_resumo ? ': ' : ''}{i.erro_resumo}
              </div>
            ) : null}
            {i.stop_reason || i.terminal_reason ? (
              <div style={{ color: C.neutralMuted, fontSize: 12, marginTop: 2 }}>
                {[i.stop_reason && `parada: ${i.stop_reason}`, i.terminal_reason && `término: ${i.terminal_reason}`].filter(Boolean).join(' · ')}
              </div>
            ) : null}
          </td>
        </tr>
      ))}
    </>
  );
}

export function DetalheDaExecucao({ id, aoBuscar, aoAbrirAgente, nomeDoProjeto = (x) => x }) {
  const s = estilos();
  const [texto, setTexto] = useState(id || '');
  useEffect(() => { setTexto(id || ''); }, [id]);
  const valido = ehUuid(id);
  const { data, isLoading, isError, error, refetch } = useObsExecucao(valido ? id : null);
  const visiveis = data?.custos_visiveis !== false;
  const cascata = useMemo(
    () => cascataDaExecucao(data?.invocacoes || [], data?.resultados || []), [data]);
  const projeto = (data?.invocacoes || []).find((i) => i.project_id)?.project_id
    || (data?.resultados || []).find((r) => r.project_id)?.project_id || null;

  const buscar = (ev) => { ev.preventDefault(); aoBuscar(texto.trim()); };
  const t = cascata.total;

  return (
    <div data-testid="obs-execucao">
      <form onSubmit={buscar} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <label style={{ ...s.rotuloDeCampo, flex: '1 1 320px' }}>
          ID da execução
          <input aria-label="ID da execução" value={texto} onChange={(e) => setTexto(e.target.value)}
            placeholder="cole o ID (UUID) da execução" style={{ ...s.campo, width: '100%', boxSizing: 'border-box' }} />
        </label>
        <button type="submit" style={s.botao}>Buscar execução</button>
      </form>

      {!id ? (
        <Vazio>Informe o ID de uma execução ou abra uma a partir da lista de Invocações.</Vazio>
      ) : !valido ? (
        <div role="alert" style={{ color: C.bad, fontSize: 14 }}>
          “{id}” não é um ID de execução válido (o ID é um UUID, como 11111111-2222-4333-8444-555555555555).
        </div>
      ) : isLoading ? <LoadingSpinner label="Carregando a execução..." />
        : isError ? (
          error?.status === 404
            ? <Vazio>Execução não encontrada — ela não existe, é de outro tenant ou foi excluída.</Vazio>
            : <QueryError error={error} onRetry={refetch} />
        ) : (
          <>
            {!visiveis ? <AvisoSemCusto /> : null}
            <div style={{ color: C.neutralDim, fontSize: 13, marginBottom: 10, overflowWrap: 'anywhere' }}>
              Execução <code style={{ color: C.text }}>{data.execution_id}</code>
              {projeto ? <> · projeto <span style={{ color: C.text }} title={projeto}>{nomeDoProjeto(projeto)}</span></> : null}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }} data-testid="total-da-execucao">
              <Kpi testId="kpi-custo-da-execucao" label="Custo total (US$)"
                value={<Custo valor={data.custo_total_usd} visivel={visiveis} completo />} />
              <Kpi label="Invocações" value={inteiro(data.total_de_invocacoes)}
                sub={`${inteiro(cascata.etapas.length)} etapa(s) · ${inteiro(data.total_de_resultados)} resultado(s) de portão`} />
              <Kpi testId="kpi-tokens-da-execucao" label="Tokens (entrada / saída)"
                value={`${inteiro(t.inputTokens)} / ${inteiro(t.outputTokens)}`}
                sub={`cache: ${inteiro(t.cacheReadTokens)} lido · ${inteiro(t.cacheCreationTokens)} criado`} />
              <Kpi label="Duração somada" value={duracao(cascata.total.duracaoMs)} sub="soma das invocações (não é o tempo de relógio)" />
              <Kpi label="Erros" value={inteiro(cascata.total.erros)}
                sub={`${inteiro(cascata.etapas.reduce((a, e) => a + e.loops, 0))} loop(s) no total`} />
            </div>

            <Titulo lado={`${inteiro(data.total_de_invocacoes)} invocação(ões), todas listadas`}>
              Cascata por etapa e rodada
            </Titulo>
            {cascata.etapas.length === 0 ? (
              <Vazio>
                Esta execução não tem invocações registradas — ela pode ser anterior ao registro
                de observabilidade (migration 0101).
              </Vazio>
            ) : cascata.etapas.map((e) => (
              <Etapa key={e.etapa} e={e} visiveis={visiveis} aoAbrirAgente={aoAbrirAgente} />
            ))}
          </>
        )}
    </div>
  );
}

export default DetalheDaExecucao;
